import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil, finalize } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';
import { StorageService } from 'src/app/services/storage.service';
import { OperatoriService } from 'src/app/platform/nomenclatoare/operatori/operatori.service';
import { MatDialog } from '@angular/material/dialog';
import { FunctieSelectionDialogComponent } from '../functie-selection-dialog/functie-selection-dialog.component';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { UserFunction } from 'src/app/platform/nomenclatoare/operatori/operatori.interface';

@Component({
    selector: 'app-job-type-switcher',
    templateUrl: './job-type-switcher.component.html',
    styleUrls: ['./job-type-switcher.component.scss']
})
export class JobTypeSwitcherComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    currentUser: any = null;
    userFunctions: UserFunction[] = [];
    activeFunction: UserFunction | null = null;
    isLoading = false;

    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    successIcon: string = environment.config.customNotifications.icons.success;
    helper = helper;

    constructor(
        private authService: AuthService,
        private storageService: StorageService,
        private operatoriService: OperatoriService,
        private notificationService: NotificationService,
        private dialog: MatDialog
    ) { }

    ngOnInit(): void {
        this.loadCurrentUser();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadCurrentUser(): void {
        this.storageService.getObject(environment.config.userKey)
            .then((user) => {
                this.currentUser = user;
                this.loadUserFunctions();
            })
            .catch((error) => {
                console.error('Error loading current user:', error);
            });
    }

    private loadUserFunctions(): void {
        if (!this.currentUser?.id_user) {
            return;
        }

        this.isLoading = true;

        this.operatoriService.getUserMultipleFunctii(this.currentUser.id_user)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe({
                next: (response: any) => {
                    this.userFunctions = response.data?.functions || [];
                    this.activeFunction = this.userFunctions.find(func => func.is_active) || null;

                    // If user has functions but no active function, show selection dialog
                    if (this.userFunctions.length > 0 && !this.activeFunction) {
                        this.showFunctionSelectionDialog();
                    }
                },
                error: (error) => {
                    console.error('Error loading user functions:', error);

                    // Don't show error for new users (404 expected)
                    if (error.status !== 404) {
                        this.notificationService.warningSwal(
                            this.errorTitle,
                            'Eroare la încărcarea funcțiilor utilizatorului',
                            this.errorIcon
                        );
                    }
                }
            });
    }

    private showFunctionSelectionDialog(): void {
        const dialogRef = this.dialog.open(FunctieSelectionDialogComponent, {
            width: '500px',
            disableClose: true,
            autoFocus: false,
            data: {
                user: this.currentUser,
                functions: this.userFunctions
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.success) {
                // Reload functions after selection
                this.loadUserFunctions();
            }
        });
    }

    shouldShowNoFunctionWarning(): boolean {
        return this.userFunctions.length === 0 && !this.isLoading;
    }

    onManageFunctions(): void {
        this.showFunctionSelectionDialog();
    }
}